import { computed, ref } from "vue";
import {
  getDashboardOverview,
  getDashboardFilters,
} from "@/services/admin/dashboard";

export function useDashboardOverview() {
  const loading = ref(false);
  const error = ref("");

  const overview = ref({
    success: false,
    meta: null,
    filters_applied: {},
    cards: {
      companies_count: 0,
      respondents_count: 0,
      roles_count: 0,
      main_indicator: {
        label: "",
        value: 0,
        display: "0",
        suffix: null,
        classification: null,
      },
    },
    comparison: {
      title: "",
      subtitle: "",
      variant: "",
      items: [],
    },
    overview_panel: {
      title: "",
      subtitle: "",
      variant: "",
      summary: {},
      segments: [],
    },
    monthly_performance: {
      title: "",
      subtitle: "",
      variant: "",
      selector: {
        type: "none",
        options: [],
        selected: null,
      },
      categories: [],
      series: [],
    },
    recent_updates: {
      title: "",
      subtitle: "",
      variant: "",
      items: [],
    },
  });

  const availableFilters = ref({
    companies: [],
    roles: [],
    versions: [],
    channels: [],
  });

  const selectedFilters = ref({
    questionnaire_id: null,
    questionnaire_version_id: null,
    company_id: null,
    role_id: null,
    channel: null,
    date_start: null,
    date_end: null,
  });

  const questionnaireOptions = computed(() => {
    const map = new Map();

    (availableFilters.value.versions || []).forEach((version) => {
      if (!map.has(version.questionnaire_id)) {
        map.set(version.questionnaire_id, {
          value: version.questionnaire_id,
          code: version.code,
          label:
            version.code?.toLowerCase() === "srq20"
              ? "SRQ-20"
              : version.code?.toLowerCase() === "srq21"
              ? "SRQ-21"
              : version.code || `Questionário ${version.questionnaire_id}`,
          response_mode: version.response_mode,
        });
      }
    });

    return Array.from(map.values());
  });

  const versionOptions = computed(() => {
    const questionnaireId = selectedFilters.value.questionnaire_id;

    return (availableFilters.value.versions || []).filter(
      (item) =>
        !questionnaireId || item.questionnaire_id === Number(questionnaireId)
    );
  });

  async function loadOverview(extraParams = {}) {
    loading.value = true;
    error.value = "";

    try {
      const params = {
        ...selectedFilters.value,
        ...extraParams,
      };

      Object.keys(params).forEach((key) => {
        if (
          params[key] === null ||
          params[key] === undefined ||
          params[key] === ""
        ) {
          delete params[key];
        }
      });

      const response = await getDashboardOverview(params);
      overview.value = response;
    } catch (err) {
      error.value =
        err?.response?.data?.message ||
        err?.message ||
        "Erro ao carregar dashboard.";
    } finally {
      loading.value = false;
    }
  }

  async function loadFilters(params = {}) {
    try {
      const response = await getDashboardFilters(params);
      availableFilters.value = response?.data || response;
    } catch (err) {
      console.error("Erro ao carregar filtros do dashboard:", err);
    }
  }

  function updateFilter(key, value) {
    selectedFilters.value = {
      ...selectedFilters.value,
      [key]: value,
    };

    if (key === "questionnaire_id") {
      selectedFilters.value.questionnaire_version_id = null;
    }
  }

  function setInitialQuestionnaire() {
    if (selectedFilters.value.questionnaire_id) return;

    const first = questionnaireOptions.value[0];
    if (!first) return;

    selectedFilters.value.questionnaire_id = first.value;
  }

  return {
    loading,
    error,
    overview,
    availableFilters,
    selectedFilters,
    questionnaireOptions,
    versionOptions,
    loadOverview,
    loadFilters,
    updateFilter,
    setInitialQuestionnaire,
  };
}
